import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    Box,
    Button,
    Typography,
    Stack,
    Grid,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    SelectChangeEvent,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import type { IInfluencer, IShippingAddress } from '../../../redux/buzzbridge/types';
import { countries } from '../../../utils/countriesList';
import PictureUploader from '../../PictureUploader/PictureUploader';
import { CustomHeaderWithStatus } from '../../EsForm/CustomHeaderWithStatus';
import { ShippingAddress } from '../../ShippingAddress/ShippingAddress';
import {
    isEphemeralSocialAvatarUrl,
    PERSIST_SOCIAL_AVATAR_FLAG,
    resolveInfluencerDisplayAvatarUrl,
    shouldAttemptSocialAvatarPersist,
} from '../../../utils/influencerSocialAvatar';

interface EditBasicInfoModalProps {
    open: boolean;
    onClose: () => void;
    influencer: IInfluencer;
    onSave: (data: Partial<IInfluencer>) => void;
}

export const EditBasicInfoModal: React.FC<EditBasicInfoModalProps> = ({ open, onClose, influencer, onSave }) => {
    const { themeStyle } = useThemeContext();
    const muiTheme = useTheme();
    const isMdUp = useMediaQuery(muiTheme.breakpoints.up('md'));

    const [formData, setFormData] = useState<Partial<IInfluencer>>({ ...influencer });
    const [originalData] = useState<Partial<IInfluencer>>({ ...influencer });

    const [profilePicture, setProfilePicture] = useState<string | undefined>(influencer.profile_picture);
    const [originalProfilePicture] = useState<string | undefined>(influencer.profile_picture);

    const [shippingLocation, setShippingLocation] = useState<IShippingAddress | undefined>(influencer.shipping_address);
    const [originalShippingLocation] = useState<IShippingAddress | undefined>(influencer.shipping_address);

    const displayAvatarUrl = resolveInfluencerDisplayAvatarUrl(influencer);
    const needsSocialAvatarPersist = shouldAttemptSocialAvatarPersist(influencer, {
        hasOwnUpload: Boolean((formData as { rawProfile?: File[] }).rawProfile?.length),
    });

    useEffect(() => {
        setFormData({ ...influencer });
        setProfilePicture(influencer.profile_picture);
        setShippingLocation(influencer.shipping_address);
    }, [influencer]);

    const hasChanged = (current: any, original: any): boolean => {
        if (Array.isArray(current) && Array.isArray(original)) {
            if (current.length !== original.length) return true;
            return current.some((item, index) => JSON.stringify(item) !== JSON.stringify(original[index]));
        }
        return JSON.stringify(current) !== JSON.stringify(original);
    };

    const hasAnyChanges = (): boolean => {
        if (needsSocialAvatarPersist) return true;
        const fields: (keyof IInfluencer)[] = ['name', 'date_of_birth', 'country', 'nationality', 'bio'];
        if (
            hasChanged(profilePicture, originalProfilePicture) ||
            hasChanged(shippingLocation, originalShippingLocation)
        )
            return true;
        return fields.some(f => hasChanged(formData[f], originalData[f]));
    };

    const handleInputChange = (field: keyof IInfluencer, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSelectChange = (field: keyof IInfluencer) => (event: SelectChangeEvent) => {
        setFormData(prev => ({ ...prev, [field]: event.target.value }));
    };

    const handleProfilePictureChange = (image: any, rawFile?: any) => {
        setProfilePicture(image);
        setFormData(prev => ({ ...prev, profile_picture: image, rawProfile: image ? rawFile : undefined }));
    };

    const handleLocationSelect = (address?: IShippingAddress) => {
        if (address) {
            setShippingLocation(address);
            setFormData(prev => ({ ...prev, shipping_address: address }));
        }
    };

    const handleSave = () => {
        const fields: (keyof IInfluencer)[] = ['name', 'date_of_birth', 'country', 'nationality', 'bio'];
        const changes: Partial<IInfluencer> = {};

        fields.forEach(f => {
            if (hasChanged(formData[f], originalData[f])) changes[f] = formData[f] as any;
        });

        if (hasChanged(profilePicture, originalProfilePicture)) {
            const hasOwnUpload = Boolean((formData as { rawProfile?: File[] }).rawProfile?.length);
            if (!isEphemeralSocialAvatarUrl(profilePicture ?? '', influencer)) {
                changes.profile_picture = profilePicture;
            }
            if (shouldAttemptSocialAvatarPersist(influencer, { hasOwnUpload })) {
                (changes as any)[PERSIST_SOCIAL_AVATAR_FLAG] = true;
            }
            if ((formData as any).rawProfile) {
                (changes as any).rawProfile = (formData as any).rawProfile;
            }
        }

        if (hasChanged(shippingLocation, originalShippingLocation)) {
            changes.shipping_address = shippingLocation;
        }

        onSave(changes);
    };

    const t = theme[themeStyle];

    const chromePaperSurface =
        themeStyle === 'dark'
            ? {
                  backgroundColor: 'rgba(18, 18, 22, 0.58)',
                  backdropFilter: 'blur(22px) saturate(165%)',
                  WebkitBackdropFilter: 'blur(22px) saturate(165%)',
                  border: `1px solid rgba(255, 255, 255, 0.14)`,
                  boxShadow: '0 24px 48px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.06)',
              }
            : {
                  backgroundColor: 'rgba(255, 255, 255, 0.78)',
                  backdropFilter: 'blur(22px) saturate(165%)',
                  WebkitBackdropFilter: 'blur(22px) saturate(165%)',
                  border: `1px solid rgba(0, 0, 0, 0.08)`,
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.85)',
              };

    const sidebarBorder = themeStyle === 'dark' ? 'rgba(255,255,255,0.08)' : t.form_divider;

    const textFieldSx = {
        '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor: t.form_divider },
            '&:hover fieldset': { borderColor: t.highlight },
            '&.Mui-focused fieldset': { borderColor: t.highlight },
        },
        '& .MuiInputLabel-root': { color: t.form_label, fontFamily: FONT_ENUM.MAIN_MEDIUM },
        '& .MuiInputLabel-root.Mui-focused': { color: t.highlight },
        '& .MuiInputBase-input': { color: t.primary_white, fontFamily: FONT_ENUM.MAIN_MEDIUM },
    };

    const selectMenuProps = {
        PaperProps: {
            sx: {
                backgroundColor: t.dropdown_black,
                border: `1px solid ${t.form_divider}`,
                borderRadius: '8px',
                '& .MuiMenuItem-root': {
                    color: t.primary_white,
                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    '&:hover': { backgroundColor: t.form_divider },
                    '&.Mui-selected': {
                        backgroundColor: t.highlight,
                        color: t.primary_white,
                        '&:hover': { backgroundColor: t.highlight },
                    },
                },
            },
        },
    };

    const selectSx = {
        '& .MuiSelect-select': { color: t.primary_white, fontFamily: FONT_ENUM.MAIN_MEDIUM },
        '& .MuiSvgIcon-root': { color: t.primary_white },
        '& fieldset': { borderColor: t.form_divider },
        '&:hover fieldset': { borderColor: t.highlight },
        '&.Mui-focused fieldset': { borderColor: t.highlight },
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            fullWidth
            fullScreen={!isMdUp}
            BackdropProps={{
                sx: {
                    backgroundColor: themeStyle === 'dark' ? 'rgba(8,8,8,0.38)' : 'rgba(255,255,255,0.48)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                },
            }}
            PaperProps={{
                sx: {
                    ...chromePaperSurface,
                    color: t.primary_white,
                    borderRadius: isMdUp ? 2 : 0,
                    maxWidth: isMdUp ? 560 : '100%',
                    width: '100%',
                    maxHeight: isMdUp ? '90vh' : '100%',
                },
            }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: { xs: 2, sm: 3 },
                    py: 2,
                    flexShrink: 0,
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton
                        onClick={onClose}
                        aria-label="Close"
                        sx={{
                            color: t.highlight,
                            '&:hover': { backgroundColor: t.form_divider },
                        }}>
                        <CloseIcon />
                    </IconButton>
                    <Typography variant="h6" sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: t.primary_white }}>
                        Edit Profile
                    </Typography>
                </Box>
                <Button
                    onClick={handleSave}
                    disabled={!hasAnyChanges()}
                    sx={{
                        color: t.highlight,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        textTransform: 'none',
                        fontSize: '1rem',
                        fontWeight: 700,
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        '&:hover': { backgroundColor: t.form_divider },
                        '&.Mui-disabled': { color: t.placeholderGray },
                    }}>
                    Save
                </Button>
            </Box>

            <Box sx={{ height: '1px', bgcolor: sidebarBorder, flexShrink: 0 }} />

            <DialogContent sx={{ p: { xs: 2, sm: 3 }, overflowY: 'auto' }}>
                <Stack spacing={3}>
                    {/* Profile Picture */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                color: t.primary_white,
                                mb: 2,
                                textAlign: 'center',
                            }}>
                            Profile Picture
                        </Typography>
                        <PictureUploader
                            initialImageSrc={profilePicture || displayAvatarUrl}
                            onChange={handleProfilePictureChange}
                            onRemove={() => handleProfilePictureChange(undefined)}
                            cropShape="round"
                            aspect={1}
                            size={160}
                            sx={{ alignItems: 'center' }}
                        />
                    </Box>

                    {/* Fields */}
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Name"
                                fullWidth
                                value={formData.name || ''}
                                onChange={e => handleInputChange('name', e.target.value)}
                                sx={textFieldSx}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Date of Birth"
                                fullWidth
                                type="date"
                                value={formData.date_of_birth || ''}
                                onChange={e => handleInputChange('date_of_birth', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                sx={textFieldSx}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel sx={{ color: t.form_label, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                    Country
                                </InputLabel>
                                <Select
                                    value={formData.country || ''}
                                    onChange={handleSelectChange('country')}
                                    label="Country"
                                    sx={selectSx}
                                    MenuProps={selectMenuProps}>
                                    {countries.map(c => (
                                        <MenuItem key={c.code} value={c.name}>
                                            {c.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel sx={{ color: t.form_label, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                    Nationality
                                </InputLabel>
                                <Select
                                    value={formData.nationality || ''}
                                    onChange={handleSelectChange('nationality')}
                                    label="Nationality"
                                    sx={selectSx}
                                    MenuProps={selectMenuProps}>
                                    {countries.map(c => (
                                        <MenuItem key={c.code} value={c.name}>
                                            {c.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Bio"
                                fullWidth
                                multiline
                                rows={4}
                                value={formData.bio || ''}
                                onChange={e => handleInputChange('bio', e.target.value)}
                                sx={textFieldSx}
                            />
                        </Grid>
                    </Grid>

                    {/* Shipping Location */}
                    <Box>
                        <CustomHeaderWithStatus
                            headerTitle="Shipping Location"
                            themeStyle={themeStyle}
                            field="shipping_address"
                            state={formData}
                            headerToolTip="This address will be used for shipping your products, so please make sure all details are correct and up to date."
                            style={{ marginBottom: '16px' }}
                        />
                        <ShippingAddress value={shippingLocation} onChange={handleLocationSelect} />
                    </Box>
                </Stack>
            </DialogContent>
        </Dialog>
    );
};
